<template>
  <div style="margin:20px">
    <FiPageRail title="Data Sync — Yards" nowrap-actions>
      <template slot="actions">
        <b-button size="sm" variant="outline-secondary" :disabled="loading" @click.prevent="load_data()"
                  aria-label="Refresh" title="Re-run the comparison">
          <i class="fa fa-refresh" aria-hidden="true"></i>
        </b-button>
      </template>
    </FiPageRail>

    <div v-if="loading" style="text-align:center">
      <br/><br/><b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

    <div v-else>
      <div class="ds-summary">
        <span class="stat-chip"><b>{{ recon_active }}</b> yards differ from TBN</span>
        <span class="stat-chip"><b>{{ active_count('missing_geofence', missing_geofence) }}</b> missing a geofence</span>
        <span class="stat-chip"><b>{{ moves_total_active }}</b> parked away</span>
      </div>

      <!-- TBN vs Fleet Int yard list -->
      <ds-section title="TBN vs Fleet Int yards" :count="recon_active" :excluded="recon_excluded"
                  :open.sync="open.recon" empty="Every yard matches between TBN and Fleet Int.">
        <div v-if="reconciliation">
          <div v-if="reconciliation.tbn_only.length" class="ds-cat-label">In TBN, not in Fleet Int</div>
          <span v-for="n in reconciliation.tbn_only" :key="'ro-' + n" class="ds-chip"
                :class="{'ds-chip-excluded': is_excluded('recon_tbn_only', n)}">
            {{ n }} <a class="ds-x" :title="is_excluded('recon_tbn_only', n) ? 'Restore' : 'Exclude permanently'"
                       @click="toggle_item('recon_tbn_only', n)">{{ is_excluded('recon_tbn_only', n) ? '&#8634;' : '&times;' }}</a>
          </span>
          <div v-if="reconciliation.fi_only.length" class="ds-cat-label">In Fleet Int, not in TBN</div>
          <span v-for="n in reconciliation.fi_only" :key="'fi-' + n" class="ds-chip"
                :class="{'ds-chip-excluded': is_excluded('recon_fi_only', n)}">
            {{ n }} <a class="ds-x" :title="is_excluded('recon_fi_only', n) ? 'Restore' : 'Exclude permanently'"
                       @click="toggle_item('recon_fi_only', n)">{{ is_excluded('recon_fi_only', n) ? '&#8634;' : '&times;' }}</a>
          </span>
        </div>
      </ds-section>

      <!-- yards missing a Samsara geofence -->
      <ds-section title="Yards missing a Samsara geofence"
                  :count="active_count('missing_geofence', missing_geofence)"
                  :excluded="excl_count('missing_geofence', missing_geofence)"
                  :open.sync="open.geofence" :unavailable="missing_geofence === null"
                  unavailable-text="Not a Samsara account." empty="Every yard has a Samsara geofence drawn.">
        <span v-for="n in missing_geofence" :key="'mg-' + n" class="ds-chip"
              :class="{'ds-chip-excluded': is_excluded('missing_geofence', n)}">
          {{ n }} <a class="ds-x" :title="is_excluded('missing_geofence', n) ? 'Restore' : 'Exclude permanently'"
                     @click="toggle_item('missing_geofence', n)">{{ is_excluded('missing_geofence', n) ? '&#8634;' : '&times;' }}</a>
        </span>
      </ds-section>

      <!-- buses parked away from their assigned yard (GPS vs TBN / Fleetio) -->
      <div class="ds-panel" v-for="sec in move_sections" :key="'mv-' + sec.key">
        <div class="ds-head">
          <span class="ds-toggle" @click="toggle_section(sec.key)">
            <span class="chev">{{ section_open(sec.key) ? '&#9660;' : '&#9654;' }}</span>
            <b>{{ moves_active(sec) }}</b> parked away from where {{ sec.system }} has them
            <span v-if="moves_excluded(sec)" class="ds-count ds-count-excluded" title="Excluded (not counted)">{{ moves_excluded(sec) }}</span>
          </span>
          <span class="ds-threshold" @click.stop>
            at least
            <b-form-select size="sm" v-model.number="min_nights" :options="threshold_options" class="ds-nights"/>
            night{{ min_nights === 1 ? '' : 's' }}
          </span>
        </div>
        <div v-if="section_open(sec.key)">
          <div v-if="sec_filtered(sec).length" class="ds-body">
            <div v-for="g in sec_groups(sec)" :key="sec.key + '-' + g.yard" class="ds-group">
              <div class="ds-group-head" @click="toggle_yard(sec.key, g.yard)">
                <span class="chev">{{ yard_open(sec, g.yard) ? '&#9660;' : '&#9654;' }}</span>
                <b>{{ g.yard }}</b> <span class="muted">{{ g.vehicles.length }} vehicle{{ g.vehicles.length === 1 ? '' : 's' }}</span>
              </div>
              <table v-if="yard_open(sec, g.yard)" class="ds-table">
                <tr><th>Vehicle</th><th>{{ sec.ref_label }}</th><th width="70px">Nights</th><th width="110px">Since</th><th width="70px"></th></tr>
                <tr v-for="m in g.vehicles" :key="'r-' + m.vehicle" :class="{'ds-row-excluded': is_excluded('moves_' + sec.key, m.vehicle)}">
                  <td><b>{{ m.vehicle }}</b></td><td>{{ m.reference_yard || '&mdash;' }}</td>
                  <td align="center">{{ m.consecutive_nights }}</td><td align="center">{{ since_label(m.since) }}</td>
                  <td align="center"><a class="ds-exclude-btn" :title="is_excluded('moves_' + sec.key, m.vehicle) ? 'Restore' : 'Exclude permanently'"
                                        @click="toggle_item('moves_' + sec.key, m.vehicle)">{{ is_excluded('moves_' + sec.key, m.vehicle) ? 'Restore' : 'Exclude' }}</a></td>
                </tr>
              </table>
            </div>
          </div>
          <div v-else class="ds-empty">No vehicle has been away from its {{ sec.system }} location for {{ min_nights }}+ nights.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {get_data_sync_yards} from '../networking/tbn_apis'
import {mapState} from 'vuex'
import moment from 'moment'
import DsSection from './data_sync_section'
import {dataSyncExclusions} from '../mixins/data_sync_exclusions'

export default {
  name: 'data_sync_yards',
  components: {DsSection},
  mixins: [dataSyncExclusions('yards')],
  data() {
    return {
      loading: false,
      reconciliation: null,
      missing_geofence: null,
      moves_tbn: null,
      moves_fleetio: null,
      exclusions: {},
      moves_min_nights: 2,
      moves_lookback_days: 14,
      min_nights: null,
      open: {recon: false, geofence: false},
      sections_open: {},
      moves_expanded: {},
    }
  },
  created() { this.load_data() },
  computed: {
    ...mapState({user: state => state.profile.user}),
    recon_active() {
      const r = this.reconciliation
      return r ? this.active_count('recon_tbn_only', r.tbn_only) + this.active_count('recon_fi_only', r.fi_only) : 0
    },
    recon_excluded() {
      const r = this.reconciliation
      return r ? this.excl_count('recon_tbn_only', r.tbn_only) + this.excl_count('recon_fi_only', r.fi_only) : 0
    },
    move_sections() {
      const out = []
      if (this.moves_tbn) out.push({key: 'tbn', system: 'TBN', ref_label: 'Home yard (TBN)', moves: this.moves_tbn})
      if (this.moves_fleetio) out.push({key: 'fleetio', system: 'Fleetio', ref_label: 'Fleetio location', moves: this.moves_fleetio})
      return out
    },
    moves_total_active() {
      return this.move_sections.reduce((n, sec) => n + this.moves_active(sec), 0)
    },
    threshold_options() {
      const max = this.moves_lookback_days || 14
      return Array.from({length: max}, (_, i) => ({value: i + 1, text: String(i + 1)}))
    },
  },
  watch: {
    'user.tenant'(n, o) { if (o && n !== o) this.load_data() },
  },
  methods: {
    async load_data(silent = false) {
      if (!silent) this.loading = true  // silent reload (exclude error resync) keeps the page in place
      try {
        const {value: {data = {}}} = await get_data_sync_yards(this.user.tenant)
        this.reconciliation = data.reconciliation || null
        this.missing_geofence = data.missing_geofence === undefined ? null : data.missing_geofence
        this.moves_tbn = data.moves_tbn === undefined ? null : data.moves_tbn
        this.moves_fleetio = data.moves_fleetio === undefined ? null : data.moves_fleetio
        this.exclusions = data.exclusions || {}
        if (data.moves_min_nights) this.moves_min_nights = data.moves_min_nights
        if (data.moves_lookback_days) this.moves_lookback_days = data.moves_lookback_days
        if (this.min_nights == null) this.min_nights = this.moves_min_nights
      } catch (e) {
        this.$emit('showAlert', 'error retrieving data, try again or contact support', 'danger', 5000)
      }
      if (!silent) this.loading = false
    },
    section_open(key) { return !!this.sections_open[key] },  // collapsed by default
    toggle_section(key) { this.sections_open = {...this.sections_open, [key]: !this.sections_open[key]} },
    sec_filtered(sec) {
      const n = this.min_nights || this.moves_min_nights
      return (sec.moves || []).filter(m => (m.consecutive_nights || 0) >= n)
    },
    moves_active(sec) {
      return this.sec_filtered(sec).filter(m => !this.is_excluded('moves_' + sec.key, m.vehicle)).length
    },
    moves_excluded(sec) {
      return this.sec_filtered(sec).filter(m => this.is_excluded('moves_' + sec.key, m.vehicle)).length
    },
    sec_groups(sec) {
      const groups = {}
      this.sec_filtered(sec).forEach(m => {
        const key = m.detected_yard || '—'
        if (!groups[key]) groups[key] = {yard: key, vehicles: []}
        groups[key].vehicles.push(m)
      })
      return Object.values(groups).sort((a, b) => b.vehicles.length - a.vehicles.length || String(a.yard).localeCompare(String(b.yard)))
    },
    yard_open(sec, yard) { return !!this.moves_expanded[sec.key + '|' + yard] },
    toggle_yard(key, yard) {
      const k = key + '|' + yard
      this.moves_expanded = {...this.moves_expanded, [k]: !this.moves_expanded[k]}
    },
    since_label(date) { return date ? moment(date, 'YYYY-MM-DD').format('MMM D') : '' },
  },
}
</script>

<style scoped>
@import './data_sync.css';
</style>
